import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { supabase } from "@/shared/lib/supabase";
import { LogOut, Mail } from "lucide-react";

const RESEND_COOLDOWN_S = 60;

export function EmailVerificationGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [cooldown, setCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState<"idle" | "sent" | "error">(
    "idle",
  );

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (!user || user.email_confirmed_at) return <>{children}</>;

  const onResend = async () => {
    if (cooldown > 0) return;
    setResendStatus("idle");
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: user.email!,
      });
      if (error) throw error;
      setResendStatus("sent");
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      console.error("Resend verification failed:", err);
      setResendStatus("error");
    }
  };

  const onSignOut = async () => {
    await supabase.auth.signOut();
    (window as any).navigate("/");
  };

  const ve = (t as any).verifyEmail ?? {};
  const banner = ve.banner ?? "Verify your email to start using Think.";
  const hint = ve.bannerHint ?? "Check your inbox for the verification link.";
  const resend = ve.resend ?? "Resend email";
  const cooldownLabel = (ve.resendCooldown ?? "Resend in {seconds}s").replace(
    "{seconds}",
    String(cooldown),
  );
  const sent = ve.resendSent ?? "Email sent — check your inbox.";
  const signOutLabel = ve.signOut ?? "Sign out";
  const tooltip = ve.blockedTooltip ?? "Verify your email first.";

  return (
    <div className="relative min-h-screen">
      <div
        className="sticky top-0 z-50 bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <div className="flex items-center gap-3 text-sm text-amber-900">
          <Mail size={18} />
          <div>
            <div className="font-semibold">{banner}</div>
            <div className="text-amber-700 text-xs">{hint}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onResend}
            disabled={cooldown > 0}
            className="px-3 py-1.5 rounded-full bg-amber-900 text-white text-xs font-semibold disabled:bg-amber-300 disabled:cursor-not-allowed"
          >
            {cooldown > 0
              ? cooldownLabel
              : resendStatus === "sent"
                ? sent
                : resendStatus === "error"
                  ? (ve.resendError ?? "Failed — try again")
                  : resend}
          </button>
          <button
            onClick={onSignOut}
            className="text-xs text-amber-900 inline-flex items-center gap-1 underline"
          >
            <LogOut size={12} />
            {signOutLabel}
          </button>
        </div>
      </div>

      <div className="relative min-h-screen">
        <div inert>{children}</div>
        <div
          className="absolute inset-0 z-40 cursor-not-allowed"
          title={tooltip}
          onClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDownCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDownCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </div>
    </div>
  );
}
